import { Injectable } from '@angular/core';
import { CapacitorHttp, HttpOptions, HttpResponse } from '@capacitor/core';

import { GlobalConfig } from '@shared/configs';
import { HttpRequestHeaderType, HttpRequestMethodType } from '@shared/enums';
import { IHttpRequestConfig, IHttpRequestResponse, IResponseData } from '@shared/types';
import { getStorage } from '@shared/utils/storage.util';

/**
 * Port of request.util.ts.
 *
 * Deliberately stays on CapacitorHttp rather than moving to Angular's
 * HttpClient: on native, CapacitorHttp runs in the platform HTTP stack, so
 * same-origin policy never applies. HttpClient runs inside the WebView, whose
 * origin is `capacitor://localhost` (iOS) or `http://localhost` (Android) —
 * every API call would need matching CORS headers that many gateways cannot
 * express for a custom scheme.
 */
@Injectable({ providedIn: 'root' })
export class HttpRequestService {
  async request<T>(configs: IHttpRequestConfig): Promise<IHttpRequestResponse<T>> {
    try {
      const configRequest = await this.setConfigs(configs);

      return await CapacitorHttp.request(configRequest as unknown as HttpOptions)
        .then((response: HttpResponse) => {
          const statusCode =
            response.status < 400 && response.status >= 200 ? 200 : response.status;

          const respData: IResponseData<T> = {
            statusCode,
            message: response.data?.message,
            data: response.data?.data ?? response.data?.result,
          };

          if (response.data?.pagination) {
            respData.pagination = response.data.pagination;
          }
          if (response.data?.metadata) {
            respData.metadata = response.data.metadata;
          }

          return { status: statusCode, data: respData } satisfies IHttpRequestResponse<T>;
        })
        .catch((err) => {
          const errResp: IHttpRequestResponse<T> = {
            status: err?.status ?? 500,
            data: {
              statusCode: err?.status ?? 500,
              message: 'Terjadi kesalahan server',
              data: undefined as T,
            },
          };

          if (err?.response !== undefined) {
            errResp.data.message = err?.response?.data?.message || err?.response?.statusText;
            errResp.data.data = err?.response?.data?.data || undefined;
          }

          return errResp;
        });
    } catch {
      return {
        status: 500,
        data: { statusCode: 500, message: 'Terjadi kesalahan', data: undefined as T },
      };
    }
  }

  private async setConfigs(source: IHttpRequestConfig): Promise<IHttpRequestConfig> {
    const token = await getStorage(GlobalConfig.KEY_TOKEN);

    /**
     * A JSON content-type is a promise of a body. The API is Fastify-backed,
     * and its parser rejects a request that makes that promise and sends
     * nothing: 400 `FST_ERR_CTP_EMPTY_JSON_BODY`, "Body cannot be empty when
     * content-type is set to 'application/json'". React stamped the header on
     * every request, which GET survives only because Fastify never body-parses
     * one — `PATCH /daily-jobs/:id` (submit) carries no payload by design and
     * failed every time.
     */
    const hasBody = source.data !== undefined && source.data !== null;

    const headers: Record<string, string | undefined | null> = source.headers
      ? { ...source.headers }
      : hasBody
        ? { 'content-type': HttpRequestHeaderType.ApplicationJson }
        : {};

    /* Also covers a caller that passes its own headers. */
    if (!hasBody) {
      for (const key of Object.keys(headers)) {
        if (key.toLowerCase() === 'content-type') {
          delete headers[key];
        }
      }
    }

    /** set Timezone */
    headers['X-Timezone'] = Intl.DateTimeFormat().resolvedOptions().timeZone;
    /** set Customer Code */
    headers['X-Customer-Code'] = GlobalConfig.ACCESS_CODE;

    /**
     * The API's guard does `authorization.split(' ')` and requires the scheme
     * to be exactly `Bearer`. React sent the bare token, which was invisible
     * there because login was the only endpoint ever called and it opts out
     * of the token entirely.
     */
    if (!source.notUseToken && token) {
      headers['authorization'] = `Bearer ${token}`;
    }

    /**
     * Strip empty headers. React sent `X-Customer-Code: null`, which
     * CapacitorHttp serializes as the literal string "null".
     */
    for (const [key, value] of Object.entries(headers)) {
      if (value === null || value === undefined || value === '') {
        delete headers[key];
      }
    }

    const apiBaseUrl = source.baseURL ? source.baseURL : GlobalConfig.URL_API;

    /**
     * Built fresh rather than mutated. React reassigned the caller's object, so
     * a config that was reused or retried had the base URL prepended twice.
     */
    const configs: IHttpRequestConfig = {
      ...source,
      url: apiBaseUrl + source.url,
      method: source.method ?? HttpRequestMethodType.GET,
      headers,
      readTimeout: source.connectTimeout
        ? source.readTimeout
        : Number(GlobalConfig.READ_TIMEOUT_API),
      connectTimeout: source.connectTimeout ?? Number(GlobalConfig.CONNECT_TIMEOUT_API),
    };

    delete configs.notUseToken;

    return configs;
  }
}
